/**
 * Which Canon camera took a photo, read from its EXIF MakerNote.
 *
 * Canon writes the model id (MakerNote tag 0x10) and the firmware revision
 * (tag 0x1e) into every JPEG, and cameras.json carries the same model id per
 * camera -- so a photo out of the camera answers "which model, which firmware
 * version" without the owner reading a menu. Both tag numbers and the BCD-ish
 * firmware decode come from reyalp's build.chdk.photos (lib/makernote.ts,
 * GPL-2.0), ported here.
 *
 * ponytail: hand-rolled container walk instead of an exif library. We need
 * exactly two integers out of one vendor tag, the path to them is ~60 lines,
 * and Flarum builds one flat forum.js -- a parser dependency would ride along
 * on every page for a button most visitors never press.
 *
 * The file is never uploaded: this runs on the bytes in the browser.
 *
 * Self-check: node ../scripts/test-exif.mjs [dir-of-jpegs]
 */

// TIFF value types, by their tag-type number.
const TYPE_SIZE = [0, 1, 1, 2, 4, 8, 1, 1, 2, 4, 8, 4, 8];

function fail(kind) {
  const e = new Error(kind);
  e.kind = kind;
  return e;
}

/** Every entry of one IFD, by tag. `value` is the raw 4-byte value/offset field. */
function readIfd(view, le, at) {
  const out = {};
  const count = view.getUint16(at, le);

  for (let i = 0; i < count; i++) {
    const off = at + 2 + i * 12;
    const type = view.getUint16(off + 2, le);

    out[view.getUint16(off, le)] = {
      size: (TYPE_SIZE[type] || 0) * view.getUint32(off + 4, le),
      value: view.getUint32(off + 8, le),
      at: off + 8,
    };
  }

  return out;
}

/** An ASCII entry, read wherever it lives: inline when it fits in 4 bytes. */
function text(view, tiff, ent) {
  const at = ent.size > 4 ? tiff + ent.value : ent.at;
  let s = '';

  for (let i = 0; i < ent.size && i < 32; i++) {
    const c = view.getUint8(at + i);
    if (!c) break;
    s += String.fromCharCode(c);
  }

  return s;
}

/**
 * The MakerNote IFD. Canon writes it in the file's own byte order, except in
 * files whose container was rewritten (Canon's press samples, raw converters):
 * those keep a little-endian MakerNote inside a big-endian TIFF. An entry
 * count that cannot be right means "read it the other way round".
 */
function makerNote(view, le, at) {
  const plausible = (order) => {
    const n = view.getUint16(at, order);
    return n > 0 && n < 256;
  };

  if (!plausible(le) && !plausible(!le)) throw fail('no_makernote');

  return readIfd(view, plausible(le) ? le : !le, at);
}

/** Offset of the TIFF header inside the APP1 (Exif) segment of a JPEG. */
function exifStart(view) {
  if (view.getUint16(0) !== 0xffd8) throw fail('not_jpeg');

  let p = 2;

  while (p + 4 <= view.byteLength) {
    if (view.getUint8(p) !== 0xff) break;

    const marker = view.getUint8(p + 1);

    // Standalone markers carry no length; SOS means the image data starts and
    // no metadata segment follows.
    if (marker >= 0xd0 && marker <= 0xd9) {
      p += 2;
      continue;
    }
    if (marker === 0xda) break;

    if (marker === 0xe1 && view.getUint32(p + 4) === 0x45786966) return p + 10; // 'Exif'

    p += 2 + view.getUint16(p + 2);
  }

  throw fail('no_exif');
}

/**
 * Canon's firmware revision string, e.g. 0x01000002 -> "100b".
 *
 * Ported from getFWRevStr(). Values outside the expected shape come back null
 * rather than throwing: an unreadable revision still leaves a usable model id,
 * and some cameras (IXUS 30, IXUS 40, S1 IS) write no revision at all.
 */
export function fwString(val) {
  const major = (val >> 24) & 0xff;
  const minor = (val >> 16) & 0xff;
  const sub = (val >> 8) & 0xff;
  const pad = minor < 0x10 ? '0' : '';

  if (minor > 0x99) return null;

  // sub 0 is the pre-release form seen on press/sample images ("b100").
  if (sub === 0) return major.toString(16) + pad + minor.toString(16);

  if (major > 0x10 || sub > 26) return null;

  return major.toString(16) + pad + minor.toString(16) + String.fromCharCode(96 + sub);
}

/**
 * { mid, fw } for a JPEG, given its first bytes (ArrayBuffer or Uint8Array).
 * Throws an Error carrying .kind: not_jpeg, no_exif, not_canon, no_makernote.
 */
export function canonId(bytes) {
  const view = new DataView(bytes.buffer || bytes, bytes.byteOffset || 0, bytes.byteLength);

  try {
    return parse(view);
  } catch (e) {
    // A truncated or malformed file walks off the end of the DataView.
    throw e.kind ? e : fail('no_exif');
  }
}

function parse(view) {
  const tiff = exifStart(view);
  const bom = view.getUint16(tiff);

  if (bom !== 0x4949 && bom !== 0x4d4d) throw fail('no_exif');

  const le = bom === 0x4949;
  const ifd0 = readIfd(view, le, tiff + view.getUint32(tiff + 4, le));

  if (!ifd0[0x010f] || text(view, tiff, ifd0[0x010f]).indexOf('Canon') !== 0) throw fail('not_canon');

  const exif = ifd0[0x8769] && readIfd(view, le, tiff + ifd0[0x8769].value);
  const note = exif && exif[0x927c];

  // Editing an image, or converting it from raw, commonly drops the MakerNote.
  if (!note || note.size <= 4) throw fail('no_makernote');

  const mn = makerNote(view, le, tiff + note.value);
  const mid = mn[0x10] && mn[0x10].value;

  if (!mid) throw fail('no_makernote');

  return { mid, fw: mn[0x1e] ? fwString(mn[0x1e].value) : null };
}
